import React, { useState, useCallback, useRef } from 'react';
import { useWeather } from '../hooks/useWeather';
import Header from '../components/Header/Header';
import Sidebar from '../components/Sidebar/Sidebar';
import AlertsBanner from '../components/AlertsBanner/AlertsBanner';
import CurrentConditions from '../components/CurrentConditions/CurrentConditions';
import MetricsGrid from '../components/MetricsGrid/MetricsGrid';
import HourlyForecast from '../components/HourlyForecast/HourlyForecast';
import WeeklyForecast from '../components/WeeklyForecast/WeeklyForecast';
import WeatherMap from '../components/WeatherMap/WeatherMap';
import TemperatureTrend from '../components/TemperatureTrend/TemperatureTrend';
import AIWeatherAgent from '../components/AIWeatherAgent/AIWeatherAgent';
import Icon from '../utils/icons';
import './Dashboard.css';

const UI_STATE_KEY = 'weatherDashboard_uiState';

const loadUIState = () => {
  try {
    const saved = localStorage.getItem(UI_STATE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const saveUIState = (key, value) => {
  try {
    const current = loadUIState();
    current[key] = value;
    localStorage.setItem(UI_STATE_KEY, JSON.stringify(current));
  } catch { /* ignore */ }
};

// No default locations - will load from localStorage or start with one default
function Dashboard() {
  const {
    locations,
    activeLocation,
    setActiveLocation,
    weatherData,
    alertsData,
    historyData,
    aqiData,
    loading,
    error,
    lastUpdated,
    addLocation,
    removeLocation,
    refresh,
    fetchHistoryForLocation
  } = useWeather(); // No initial locations - hook handles persistence

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = loadUIState();
    return saved.sidebarOpen !== undefined ? saved.sidebarOpen : true;
  });

  // Track whether the AI agent succeeded — if so, hide the NWS alerts banner
  // (the AI response already incorporates alert info into its briefing)
  const [aiSucceeded, setAiSucceeded] = useState(false);
  const handleAiStatus = useCallback((status) => {
    setAiSucceeded(status === 'success');
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => {
      const next = !prev;
      saveUIState('sidebarOpen', next);
      return next;
    });
  }, []);

  const activeWeather = activeLocation ? weatherData[activeLocation] : null;
  const activeAlerts = activeLocation ? (alertsData[activeLocation] || []) : [];

  return (
    <div className={`dashboard ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Sidebar
        isOpen={sidebarOpen}
        locations={locations}
        activeLocation={activeLocation}
        weatherData={weatherData}
        onSelectLocation={setActiveLocation}
        onAddLocation={addLocation}
        onRemoveLocation={removeLocation}
        onToggle={toggleSidebar}
        loading={loading}
      />

      <main className="main-content">
        <Header
          location={activeLocation}
          lastUpdated={lastUpdated}
          onRefresh={refresh}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <span><Icon name="alertTriangle" size={16} /> {error}</span>
            <button onClick={refresh}>Retry</button>
          </div>
        )}

        {loading && !activeWeather ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading weather data...</p>
          </div>
        ) : activeWeather ? (
          <>
            {!aiSucceeded && <AlertsBanner alerts={activeAlerts} />}

            <div className="dashboard-grid">
              <div className="grid-item ai-agent">
                <AIWeatherAgent
                  activeLocation={activeLocation}
                  weatherData={activeWeather}
                  onStatusChange={handleAiStatus}
                />
              </div>

              <div className="grid-item current-conditions">
                <CurrentConditions data={activeWeather.current} />
              </div>

              <div className="grid-item metrics">
                <MetricsGrid data={activeWeather.current} aqi={activeLocation ? aqiData[activeLocation] : null} />
              </div>

              <div className="grid-item hourly">
                <HourlyForecast data={activeWeather.hourly} />
              </div>

              <div className="grid-item weather-map">
                <WeatherMap
                  coordinates={activeWeather.current?.coordinates}
                  location={activeLocation}
                  weatherData={activeWeather.current}
                />
              </div>

              <div className="grid-item weekly">
                <WeeklyForecast data={activeWeather.forecast} />
              </div>

              <div className="grid-item temperature-trend">
                <TemperatureTrend
                  hourlyData={activeWeather.hourly}
                  forecastData={activeWeather.forecast}
                  activeLocation={activeLocation}
                  historyData={historyData}
                  onFetchHistory={fetchHistoryForLocation}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <Icon name="globe" size={64} className="empty-icon" />
            <h2>No Location Selected</h2>
            <p>Add a location using the sidebar to get started</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
